"use client"

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import { useRouter } from "next/navigation"
import { isAuthRetryableFetchError } from "@supabase/supabase-js"
import { createBrowserClient } from "@forestsync/auth/browser"
import { useUiMode } from "@/components/ui-mode/UiModeProvider"
import { fetchAuthData, type AuthData } from "@/actions/auth-context"
import type { MarketSummary } from "@/lib/queries"

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

interface AuthContextValue {
  isLoading: boolean
  isAuthenticated: boolean
  userId: string | null
  userEmail: string | null
  orgRole: number | null
  stripeOnboardingComplete: boolean | null
  /** Onboarding complete but Stripe paused the account (requirements due) */
  stripeRestricted: boolean
  markets: MarketSummary[]
  tradingEnabled: boolean
  viewOnly: boolean
  orgLogoUrl: string | null
  orgName: string | null
  /** Initiates client-side signout. onAuthStateChange(SIGNED_OUT) handles cleanup. */
  signOut: () => Promise<void>
  /** Force a fresh fetch of all auth context data (e.g. after profile update). */
  refreshAuthData: () => Promise<void>
}

const UNAUTHENTICATED: Omit<AuthContextValue, "signOut" | "refreshAuthData"> = {
  isLoading: false,
  isAuthenticated: false,
  userId: null,
  userEmail: null,
  orgRole: null,
  stripeOnboardingComplete: null,
  stripeRestricted: false,
  markets: [],
  tradingEnabled: false,
  viewOnly: false,
  orgLogoUrl: null,
  orgName: null,
}

function stateFromData(data: AuthData): Omit<AuthContextValue, "signOut" | "refreshAuthData"> {
  return {
    isLoading: false,
    isAuthenticated: true,
    userId: data.userId,
    userEmail: data.email,
    orgRole: data.orgRole,
    stripeOnboardingComplete: data.stripeOnboardingComplete,
    stripeRestricted: data.stripeRestricted,
    markets: data.markets,
    tradingEnabled: data.tradingEnabled,
    viewOnly: data.viewOnly,
    orgLogoUrl: data.orgLogoUrl,
    orgName: data.orgName,
  }
}

const AuthContext = createContext<AuthContextValue>({
  ...UNAUTHENTICATED,
  signOut: async () => {},
  refreshAuthData: async () => {},
})

export function useAuth() {
  return useContext(AuthContext)
}

/** Re-verify the session when a tab returns after being hidden at least this long. */
const REVALIDATE_AFTER_HIDDEN_MS = 5 * 60_000
const RELOAD_GUARD_KEY = "auth-build-reload-at"

/** Hard navigation so router-cached authenticated payloads are dropped too. */
function redirectToLoginExpired() {
  if (window.location.pathname.startsWith("/login")) return
  const next = encodeURIComponent(window.location.pathname)
  window.location.assign(`/login?reason=expired&next=${next}`)
}

/**
 * fetchAuthData() threw instead of returning — almost always a tab still running an
 * older deployment whose Server Action IDs no longer exist ("Failed to find Server
 * Action"). Reload once to pick up the new build; the guard prevents a reload loop
 * when the failure is something else (e.g. DB outage). Returns true if reloading.
 */
function reloadForNewBuild(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0)
    if (Date.now() - last < 60_000) return false
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()))
  } catch {
    return false
  }
  window.location.reload()
  return true
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * AuthProvider is the primary source of truth for all auth-sensitive UI state.
 *
 * Architecture:
 *   - Seeded with server-fetched `initialData` for zero-flash hydration on initial load
 *   - Verifies the session client-side on mount to self-correct CDN-cached stale RSC
 *   - Reacts to Supabase auth events without router.refresh() — all state updates
 *     are purely client-side data fetches via the fetchAuthData() server action
 *   - proxy.ts remains the hard security gate for protected routes (server-side)
 *
 * Auth event handling:
 *   SIGNED_IN      → fetch fresh auth data, update context (no router.refresh)
 *   SIGNED_OUT     → clear context; user-initiated → home, otherwise the session died
 *                    (revoked refresh token, signed out elsewhere, deleted user) →
 *                    /login with an "expired" notice
 *   TOKEN_REFRESHED → no-op (same user, same data)
 *
 * Stale-session handling: a browser can hold auth cookies the server no longer accepts.
 * Without reconciling, the UI half-works (guest header, no sidebar, actions fail with
 * "Not authenticated"). Whenever the server reports no session while the browser has
 * one, the local session is purged and the user is sent to /login explicitly.
 */
export function AuthProvider({
  children,
  initialData,
}: {
  children: React.ReactNode
  initialData: AuthData | null
}) {
  const router = useRouter()
  const { syncMode } = useUiMode()

  const [state, setState] = useState<Omit<AuthContextValue, "signOut" | "refreshAuthData">>(
    () => initialData
      ? stateFromData(initialData)
      : { ...UNAUTHENTICATED, isLoading: true }
  )

  const stateRef = useRef(state)
  useEffect(() => { stateRef.current = state }, [state])

  const applyAuthData = useCallback((data: AuthData) => {
    // Sync UiModeProvider so sidebar visibility is always consistent with the
    // DB-stored mode, correcting any stale initialMode from a CDN-cached RSC.
    syncMode(data.uiMode)
    setState(stateFromData(data))
  }, [syncMode])

  const clearAuthState = useCallback(() => {
    setState(UNAUTHENTICATED)
  }, [])

  const userSignOutRef = useRef(false)

  // Server rejected the browser's session. Confirm with Supabase Auth directly so a
  // transient network/5xx failure never logs anyone out, then purge the local session.
  // signOut fires SIGNED_OUT, which performs the redirect.
  const endExpiredSession = useCallback(async () => {
    const supabase = createBrowserClient()
    const { error } = await supabase.auth.getUser()
    const transient = !error || isAuthRetryableFetchError(error) || !error.status || error.status >= 500
    if (transient) {
      setState(prev => ({ ...prev, isLoading: false }))
      return
    }
    clearAuthState()
    await supabase.auth.signOut({ scope: "local" })
  }, [clearAuthState])

  const resolveAuthData = useCallback(async (silent: boolean) => {
    if (!silent) setState(prev => ({ ...prev, isLoading: true }))
    let data: AuthData | null
    try {
      data = await fetchAuthData()
    } catch {
      if (reloadForNewBuild()) return
      // Keep the last known state — proxy.ts remains the hard security gate.
      setState(prev => ({ ...prev, isLoading: false }))
      return
    }
    if (data) applyAuthData(data)
    else await endExpiredSession()
  }, [applyAuthData, endExpiredSession])

  const loadAuthData = useCallback(() => resolveAuthData(false), [resolveAuthData])

  const signOut = useCallback(async () => {
    // Sign out via the browser client so onAuthStateChange fires SIGNED_OUT immediately.
    // The Supabase browser client sends the invalidation request to Supabase Auth
    // servers AND clears the session cookie — no server action is needed.
    // scope "local" ends only this device's session. The default ("global") revokes the
    // user's sessions everywhere, which silently breaks every other open browser.
    userSignOutRef.current = true
    const supabase = createBrowserClient()
    await supabase.auth.signOut({ scope: "local" })
  }, [])

  useEffect(() => {
    const supabase = createBrowserClient()

    // On mount: verify the browser session against what the server rendered.
    // Self-corrects CDN-cached stale RSC payloads without router.refresh().
    supabase.auth.getSession().then(({ data: { session } }) => {
      const hasSession = !!session
      const hadInitialData = !!initialData

      if (hasSession && !hadInitialData) {
        // Server said guest but browser has a valid session (CDN cache hit) — fetch real data
        loadAuthData()
      } else if (!hasSession && hadInitialData) {
        // Server said authenticated but session is gone (expired between SSR and hydration)
        clearAuthState()
      } else {
        // Both agree (guest/guest or auth/auth) — just clear the loading spinner
        if (!hasSession) clearAuthState()
      }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, _session) => {
      if (event === "SIGNED_IN") {
        // Do NOT call router.refresh() — it conflicts with ongoing navigations and causes
        // "unexpected response" errors. Fetch data client-side and update context directly.
        await loadAuthData()
      } else if (event === "SIGNED_OUT") {
        clearAuthState()
        if (userSignOutRef.current) {
          userSignOutRef.current = false
          router.push("/")
        } else {
          redirectToLoginExpired()
        }
      } else if (event === "USER_UPDATED") {
        // Refresh context when email/password/metadata changes via supabase.auth.updateUser()
        await loadAuthData()
      }
      // TOKEN_REFRESHED, PASSWORD_RECOVERY — no action needed
    })

    // A tab left open can outlive its session (signed out elsewhere, token revoked).
    // Re-verify when it comes back so the user learns before an action fails.
    let hiddenAt = 0
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now()
      } else if (hiddenAt && Date.now() - hiddenAt >= REVALIDATE_AFTER_HIDDEN_MS && stateRef.current.isAuthenticated) {
        resolveAuthData(true)
      }
    }
    document.addEventListener("visibilitychange", onVisibilityChange)

    return () => {
      subscription.unsubscribe()
      document.removeEventListener("visibilitychange", onVisibilityChange)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <AuthContext.Provider value={{ ...state, signOut, refreshAuthData: loadAuthData }}>
      {children}
    </AuthContext.Provider>
  )
}
